<template>
  <div class="wizard-body small-9 columns">
    <page-header
      :header-title="$t('INBOX_MGMT.ADD.MULTIMEDIA_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.MULTIMEDIA_CHANNEL.DESC')"
    />
    <form class="row" @submit.prevent="createChannel()">
      <div class="medium-8 columns">
        <label :class="{ error: $v.channelName.$error }">
          {{ $t('INBOX_MGMT.ADD.MULTIMEDIA_CHANNEL.CHANNEL_NAME.LABEL') }}
          <input
            v-model.trim="channelName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.MULTIMEDIA_CHANNEL.CHANNEL_NAME.PLACEHOLDER')
            "
            @blur="$v.channelName.$touch"
          />
          <span v-if="$v.channelName.$error" class="message">{{
            $t('INBOX_MGMT.ADD.MULTIMEDIA_CHANNEL.CHANNEL_NAME.ERROR')
          }}</span>
        </label>
      </div>

      <div class="medium-8 columns">
        <label>
          Tipo de mídia
          <select
            v-model="channelMidiaType"
            placeholder="Selecione a origem dos dados"
          >
            <option v-for="(item, index) in mediaTypes" :key="item" :value="item">
              {{ $t(`INBOX_MGMT.ADD.MULTIMEDIA_CHANNEL.PROVIDERS.${index}.LABEL`) }}
            </option>
          </select>
        </label>
      </div>

      <MultimediaOriginConfig
        v-if="showMultimedia"
        :config="custom_config"
        :origin.sync="origin"
        :validator="$v"
        provider-key="MULTIMEDIA"
      />
      <MultimediaOriginConfig
        v-if="showExportWpp"
        :config="custom_config"
        :origin.sync="origin"
        :validator="$v"
        provider-key="EXPORT_WPP"
      />

      <div class="medium-12 columns">
        <woot-submit-button
          :loading="uiFlags.isCreating"
          :button-text="$t('INBOX_MGMT.ADD.MULTIMEDIA_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import multimediaMixin, {
  MIDIA_TYPES,
  ORIGIN_CREDENTIAL_CONFIGS,
} from 'shared/mixins/multimediaMixin';
import { required, requiredIf } from 'vuelidate/lib/validators';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader';
import getUuid from 'widget/helpers/uuid';
import MultimediaOriginConfig from './multimediaChannels/MultimediaOriginConfig.vue';

// Pre-compute which origins require each credential field key
const _fieldRequiredByOrigin = {};
Object.entries(ORIGIN_CREDENTIAL_CONFIGS).forEach(([originValue, config]) => {
  if (config?.type === 'fields' && config.fields) {
    config.fields.forEach(field => {
      if (!_fieldRequiredByOrigin[field.key]) {
        _fieldRequiredByOrigin[field.key] = [];
      }
      _fieldRequiredByOrigin[field.key].push(originValue);
    });
  }
});

// Collect all credential field keys across all origins to initialize custom_config
const _allCredentialFieldDefaults = {};
Object.values(ORIGIN_CREDENTIAL_CONFIGS).forEach(config => {
  if (config?.initialState) {
    const sample = config.initialState(null);
    Object.entries(sample).forEach(([key, val]) => {
      if (!Object.prototype.hasOwnProperty.call(_allCredentialFieldDefaults, key)) {
        _allCredentialFieldDefaults[key] = val;
      }
    });
  }
});

export default {
  name: 'Multimedia',
  components: {
    PageHeader,
    MultimediaOriginConfig,
  },
  mixins: [alertMixin, multimediaMixin],
  data() {
    return {
      channelName: '',
      mediaTypes: MIDIA_TYPES,
      channelMidiaType: 'webhook',
      origin: 'gcs_intelia',
      custom_config: {
        bucket: '',
        prefix: '',
        credentials: null,
        credentials_filename: '',
        ..._allCredentialFieldDefaults,
      },
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    showMultimedia() {
      return this.channelMidiaType === 'multimedia';
    },
    showWebhook() {
      return this.channelMidiaType === 'webhook';
    },
    showExportWpp() {
      return this.channelMidiaType === 'export_wpp';
    },
  },
  validations() {
    const base = { channelName: { required } };

    if (this.showExportWpp || this.showMultimedia) {
      base.origin = { required };

      const credentialValidations = {};

      // File-based credential validation (gcs_external)
      credentialValidations.credentials = {
        required: requiredIf(function () {
          return this.origin === 'gcs_external';
        }),
      };
      credentialValidations.credentials_filename = {};

      // Bucket validation (required for all origins that use a bucket)
      const bucketValidation = {
        required: requiredIf(function () {
          const config = ORIGIN_CREDENTIAL_CONFIGS[this.origin];
          return config?.usesBucket !== false && this.origin !== 'gcs_intelia';
        }),
      };

      // Dynamic field validations derived from ORIGIN_CREDENTIAL_CONFIGS
      Object.entries(_fieldRequiredByOrigin).forEach(([fieldKey, originsRequiring]) => {
        credentialValidations[fieldKey] = {
          required: requiredIf(function () {
            return originsRequiring.includes(this.origin);
          }),
        };
      });

      base.custom_config = {
        bucket: bucketValidation,
        prefix: {},
        ...credentialValidations,
      };
    }

    return base;
  },
  watch: {
    origin(newOrigin) {
      // Clear all credential fields across all origins
      Object.values(ORIGIN_CREDENTIAL_CONFIGS).forEach(config => {
        if (config?.type === 'fields' && config.fields) {
          config.fields.forEach(field => {
            this.custom_config[field.key] = '';
          });
        } else if (config?.type === 'file') {
          this.custom_config.credentials = null;
          this.custom_config.credentials_filename = '';
        }
      });

      // Apply the new origin's initial state
      const newConfig = ORIGIN_CREDENTIAL_CONFIGS[newOrigin];
      if (newConfig?.initialState) {
        Object.assign(this.custom_config, newConfig.initialState(null));
      }
    },
  },
  methods: {
    buildCustomConfigForSubmit() {
      const credConfig = ORIGIN_CREDENTIAL_CONFIGS[this.origin];
      const config = {
        bucket: credConfig?.usesBucket !== false ? (this.custom_config.bucket || '') : '',
        prefix: this.custom_config.prefix || '',
      };

      if (credConfig?.type === 'fields') {
        const credentials = {};
        credConfig.fields.forEach(field => {
          const val = this.custom_config[field.key];
          credentials[field.key] = field.isInteger
            ? (parseInt(val, 10) || 0)
            : (val || '');
        });
        config.credentials = credentials;
      } else if (credConfig?.type === 'file') {
        const creds = this.custom_config.credentials;
        if (creds && typeof creds === 'object' && !Array.isArray(creds)) {
          config.credentials = { ...creds };
        }
        if (this.custom_config.credentials_filename) {
          config.credentials_filename = this.custom_config.credentials_filename;
        }
      }

      return config;
    },
    async createChannel() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      try {
        const customConfig = this.buildCustomConfigForSubmit();
        const multimediaChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.channelName,
            channel: {
              type: 'multimedia',
              midia: this.channelMidiaType,
              origin: this.origin,
              identifier: getUuid(),
              custom_config: customConfig,
            },
          }
        );

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: multimediaChannel.id,
          },
        });
      } catch (error) {
        this.showAlert(
          this.$t('INBOX_MGMT.ADD.MULTIMEDIA_CHANNEL.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>
